import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader, AlertCircle } from 'lucide-react';
import { documentService } from '../../services/api';

const CollaboraEditor = ({ documentId, isOpen, onClose, onSave }) => {
  const [editorUrl, setEditorUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  const fetchEditorUrl = useCallback(async () => {
    if (!documentId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.getEditUrl(documentId);
      setEditorUrl(response.data.editor_url);
    } catch (err) {
      console.error('Failed to get editor URL:', err);
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to open document editor. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (isOpen && documentId) {
      fetchEditorUrl();
    }
    return () => {
      setEditorUrl(null);
      setError(null);
    };
  }, [isOpen, documentId, fetchEditorUrl]);

  // Listen for PostMessage events from Collabora
  // Track whether the doc has been modified — Collabora fires Modified=false
  // on initial load too, which we must NOT treat as a save event (it would
  // trigger an infinite reload loop if onSave refetches/re-renders).
  const hasBeenModifiedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    hasBeenModifiedRef.current = false;

    const handleSaved = () => {
      if (hasBeenModifiedRef.current && onSave) {
        hasBeenModifiedRef.current = false;
        onSave();
      }
    };

    const handleMessage = (event) => {
      // Collabora sends structured PostMessage events
      let msg = event.data;
      if (typeof msg === 'string') {
        try {
          msg = JSON.parse(msg);
        } catch {
          return;
        }
      }
      if (!msg?.MessageId) return;

      if (msg.MessageId === 'UI_Close') {
        onClose();
      } else if (msg.MessageId === 'Doc_ModifiedStatus') {
        if (msg.Values?.Modified === true) {
          hasBeenModifiedRef.current = true;
        } else if (msg.Values?.Modified === false) {
          handleSaved();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, onClose, onSave]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center p-2">
      <div className="bg-white rounded-lg shadow-2xl w-[95vw] h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Document Editor</span>
            {loading && (
              <span className="inline-flex items-center text-xs text-gray-500">
                <Loader className="h-3 w-3 animate-spin mr-1" />
                Loading...
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            title="Close Editor"
            aria-label="Close Editor"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="text-center">
                <Loader className="h-8 w-8 animate-spin text-yellow-500 mx-auto" />
                <p className="mt-3 text-sm text-gray-600">Opening document editor...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="text-center max-w-md px-4">
                <AlertCircle className="h-10 w-10 text-red-400 mx-auto" />
                <p className="mt-3 text-sm text-red-600">{error}</p>
                <div className="mt-4 flex gap-3 justify-center">
                  <button
                    onClick={fetchEditorUrl}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm font-medium transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {editorUrl && !loading && !error && (
            <iframe
              ref={iframeRef}
              src={editorUrl}
              title="Document Editor"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              allow="clipboard-read; clipboard-write"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaboraEditor;
